import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EditRegistrationModal from './EditRegistrationModal';

const { FiLoader, FiRefreshCw, FiDownload, FiFilter, FiSearch, FiCalendar, FiUsers, FiClock, FiEdit, FiTrash2, FiAlertCircle, FiX, FiCheck, FiSquare, FiCheckSquare, FiAlertTriangle } = FiIcons;

const Registrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterWeek, setFilterWeek] = useState('');
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);
  const [isMultiDeleteModalOpen, setIsMultiDeleteModalOpen] = useState(false);
  const [multiDeleteLoading, setMultiDeleteLoading] = useState(false);
  const [multiDeleteSuccess, setMultiDeleteSuccess] = useState(false);

  // Einfache Funktion zum direkten Laden der Anmeldungen aus Supabase
  const loadRegistrations = async () => {
    setIsLoading(true);
    setError(null);
    setSelectedRegistrations([]);
    try {
      console.log("Direkt von Supabase laden...");
      const { data, error } = await supabase
        .from('registrations_a7b9c8d6e5')
        .select('*')
        .order('registration_date', { ascending: false });

      if (error) {
        console.error("Fehler beim Laden der Anmeldungen:", error);
        setError(`Fehler beim Laden: ${error.message}`);
        return;
      }

      console.log(`${data?.length || 0} Anmeldungen geladen`);
      setRegistrations(data || []);
    } catch (err) {
      console.error("Unerwarteter Fehler:", err);
      setError(`Unerwarteter Fehler: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Beim ersten Laden der Komponente
  useEffect(() => {
    loadRegistrations();
  }, []);

  // Filter anwenden
  const filteredRegistrations = registrations.filter(reg => {
    // Woche-Filter (aktuelle/nächste)
    if (filterWeek === 'current' && reg.is_next_week) {
      return false;
    }
    if (filterWeek === 'next' && !reg.is_next_week) {
      return false;
    }
    
    // Tag-Filter
    if (filterDay && (!reg.day || !reg.day.toLowerCase().includes(filterDay.toLowerCase()))) {
      return false;
    }

    // Datum-Filter
    if (filterDate && reg.menu_date) {
      if (!reg.menu_date.startsWith(filterDate)) {
        return false;
      }
    }

    // Suchfilter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        (reg.name && reg.name.toLowerCase().includes(searchLower)) ||
        (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
        (reg.phone && reg.phone.toLowerCase().includes(searchLower)) ||
        (reg.menu_name && reg.menu_name.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  // Formatiert ein Datum
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE') + ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return dateString || 'Kein Datum';
    }
  };

  // Formatiert ein Menüdatum
  const formatMenuDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE');
    } catch (error) {
      return '';
    }
  };

  // Unique Tage für den Filter bekommen
  const getDayOptions = () => {
    const uniqueDays = [...new Set(registrations.filter(r => r.day).map(r => r.day))];
    return uniqueDays.sort();
  };

  // Unique Menüdaten für den Filter bekommen
  const getMenuDateOptions = () => {
    const uniqueDates = [...new Set(registrations
      .filter(r => r.menu_date)
      .map(r => r.menu_date)
      .sort())];
      
    return uniqueDates.map(date => ({
      value: date.substring(0, 10), // YYYY-MM-DD
      label: formatMenuDate(date)
    }));
  };

  // CSV Export
  const exportToCsv = () => {
    // Headers
    const headers = [
      'Name', 'Kontakt', 'Tag', 'Menü', 'Menüdatum', 'Personen', 'Zeit', 'Gesamtpreis', 'Vorspeise', 'Anmeldedatum', 'Woche'
    ];

    // Daten formatieren
    const csvData = filteredRegistrations.map(reg => [
      reg.name || '',
      reg.email || reg.phone || '',
      reg.day || '',
      reg.menu_name || '',
      formatMenuDate(reg.menu_date) || '',
      reg.persons || 0,
      reg.time || '',
      reg.total_price ? `CHF ${reg.total_price.toFixed(2)}` : '',
      reg.preference === 'suppe' ? 'Suppe' : 'Salat',
      formatDate(reg.registration_date),
      reg.is_next_week ? 'Nächste Woche' : 'Aktuelle Woche'
    ]);

    // CSV-Inhalt erstellen
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download auslösen
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Download-Attribute setzen
    const today = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `anmeldungen_${today}.csv`);
    
    // Download starten
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Funktion zum Bearbeiten einer Anmeldung
  const handleEdit = (registration) => {
    setEditingRegistration(registration);
  };

  // Funktion zum Speichern einer bearbeiteten Anmeldung
  const handleSaveEdit = async (updatedRegistration) => {
    try {
      const { data, error } = await supabase
        .from('registrations_a7b9c8d6e5')
        .update({
          name: updatedRegistration.name,
          email: updatedRegistration.contactMethod === 'email' ? updatedRegistration.email : null,
          phone: updatedRegistration.contactMethod === 'phone' ? updatedRegistration.phone : null,
          persons: updatedRegistration.persons,
          time: updatedRegistration.time,
          preference: updatedRegistration.preference,
          contact_method: updatedRegistration.contactMethod,
          total_price: updatedRegistration.total_price
        })
        .eq('id', updatedRegistration.id)
        .select();

      if (error) {
        throw error;
      }

      console.log("Anmeldung aktualisiert:", data);
      
      // Aktualisiere die Liste der Anmeldungen
      setRegistrations(prevRegistrations => 
        prevRegistrations.map(reg => 
          reg.id === updatedRegistration.id ? {...reg, ...updatedRegistration} : reg
        )
      );
      
      setEditingRegistration(null);
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Anmeldung:", error);
      setError(`Fehler beim Aktualisieren: ${error.message}`);
    }
  };

  // Funktion zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setEditingRegistration(null);
  };

  // Funktion zum Öffnen des Lösch-Dialogs
  const handleDeleteClick = (registration) => {
    setRegistrationToDelete(registration);
    setIsDeleteModalOpen(true);
    setDeleteSuccess(false);
  };

  // Verbesserte Funktion zum Löschen einer Anmeldung
  const handleDelete = async () => {
    if (!registrationToDelete) return;
    
    setDeleteLoading(true);
    setError(null);
    
    try {
      const registrationId = registrationToDelete.id;
      console.log("Versuche Anmeldung zu löschen:", registrationId);
      
      // Direkte Löschung mit anschließender Bestätigung
      const { error } = await supabase
        .from('registrations_a7b9c8d6e5')
        .delete()
        .eq('id', registrationId);
        
      if (error) {
        console.error("Löschfehler:", error);
        throw error;
      }
      
      console.log("Anmeldung erfolgreich gelöscht:", registrationId);
      
      // Entferne die gelöschte Anmeldung aus dem lokalen State
      setRegistrations(prevRegistrations => 
        prevRegistrations.filter(reg => reg.id !== registrationId)
      );
      
      setDeleteSuccess(true);
      
      // Schließe den Dialog nach kurzer Verzögerung
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setRegistrationToDelete(null);
        setDeleteSuccess(false);
      }, 1500);
      
    } catch (error) {
      console.error("Fehler beim Löschen der Anmeldung:", error);
      setError(`Fehler beim Löschen: ${error.message}`);
      setDeleteSuccess(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Checkbox-Funktionen für Mehrfachauswahl
  const handleCheckboxChange = (registrationId) => {
    setSelectedRegistrations(prev => {
      if (prev.includes(registrationId)) {
        return prev.filter(id => id !== registrationId);
      } else {
        return [...prev, registrationId];
      }
    });
  };

  // Alle Checkboxen umschalten
  const handleSelectAll = () => {
    if (selectedRegistrations.length === filteredRegistrations.length) {
      // Wenn alle ausgewählt sind, alle abwählen
      setSelectedRegistrations([]);
    } else {
      // Sonst alle auswählen
      setSelectedRegistrations(filteredRegistrations.map(reg => reg.id));
    }
  };

  // Funktion zum Öffnen des Multi-Lösch-Dialogs
  const handleMultiDeleteClick = () => {
    if (selectedRegistrations.length === 0) return;
    setIsMultiDeleteModalOpen(true);
    setMultiDeleteSuccess(false);
  };

  // Funktion zum Löschen mehrerer Anmeldungen
  const handleMultiDelete = async () => {
    if (selectedRegistrations.length === 0) return;
    
    setMultiDeleteLoading(true);
    setError(null);
    
    try {
      console.log("Versuche mehrere Anmeldungen zu löschen:", selectedRegistrations);
      
      // Löschen aller ausgewählten Anmeldungen
      const { error } = await supabase
        .from('registrations_a7b9c8d6e5')
        .delete()
        .in('id', selectedRegistrations);
        
      if (error) {
        console.error("Mehrfach-Löschfehler:", error);
        throw error;
      }
      
      console.log("Anmeldungen erfolgreich gelöscht:", selectedRegistrations.length);
      
      // Entferne die gelöschten Anmeldungen aus dem lokalen State
      setRegistrations(prevRegistrations => 
        prevRegistrations.filter(reg => !selectedRegistrations.includes(reg.id))
      );
      
      setMultiDeleteSuccess(true);
      
      // Zurücksetzen der Auswahl
      setSelectedRegistrations([]);
      
      // Schließe den Dialog nach kurzer Verzögerung
      setTimeout(() => {
        setIsMultiDeleteModalOpen(false);
        setMultiDeleteSuccess(false);
      }, 1500);
      
    } catch (error) {
      console.error("Fehler beim Löschen mehrerer Anmeldungen:", error);
      setError(`Fehler beim Löschen: ${error.message}`);
      setMultiDeleteSuccess(false);
    } finally {
      setMultiDeleteLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Anmeldungen</h1>
          <p className="text-gray-600">Übersicht aller Anmeldungen</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadRegistrations}
            disabled={isLoading}
            className={`flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <SafeIcon icon={isLoading ? FiLoader : FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
            <span>Aktualisieren</span>
          </button>
          <button
            onClick={exportToCsv}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <SafeIcon icon={FiDownload} />
            <span>CSV Export</span>
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Suche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Suche
            </label>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Name, E-Mail, Telefon..."
              />
            </div>
          </div>

          {/* Woche-Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Woche filtern
            </label>
            <div className="relative">
              <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterWeek}
                onChange={(e) => setFilterWeek(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Alle Wochen</option>
                <option value="current">Aktuelle Woche</option>
                <option value="next">Nächste Woche</option>
              </select>
            </div>
          </div>

          {/* Tag-Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag filtern
            </label>
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterDay}
                onChange={(e) => setFilterDay(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Alle Tage</option>
                {getDayOptions().map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Datum-Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Menüdatum filtern
            </label>
            <div className="relative">
              <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Alle Daten</option>
                {getMenuDateOptions().map(date => (
                  <option key={date.value} value={date.value}>{date.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Anzahl der Ergebnisse und Mehrfachauswahl-Aktionen */}
        <div className="mt-4 flex flex-wrap items-center justify-between">
          <div className="text-gray-600 text-sm">
            {filteredRegistrations.length} {filteredRegistrations.length === 1 ? 'Anmeldung' : 'Anmeldungen'} gefunden
          </div>
          {selectedRegistrations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center mt-2 sm:mt-0"
            >
              <span className="text-sm font-medium text-gray-700 mr-3">
                {selectedRegistrations.length} {selectedRegistrations.length === 1 ? 'Anmeldung' : 'Anmeldungen'} ausgewählt
              </span>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleMultiDeleteClick}
                className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
                <span className="text-sm">Löschen</span>
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Fehlermeldung */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Anmeldungsliste */}
      {isLoading ? (
        <div className="bg-white rounded-2xl shadow-md p-8 flex items-center justify-center">
          <SafeIcon icon={FiLoader} className="text-primary-500 text-2xl mr-3 animate-spin" />
          <p className="text-gray-600">Anmeldungen werden geladen...</p>
        </div>
      ) : filteredRegistrations.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <div
                      onClick={handleSelectAll}
                      className="flex items-center cursor-pointer hover:text-primary-600"
                    >
                      <SafeIcon
                        icon={selectedRegistrations.length === filteredRegistrations.length ? FiCheckSquare : FiSquare}
                        className="mr-1 text-gray-500 hover:text-primary-500"
                      />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kontakt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Menü
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zeit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Anmeldung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Woche
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRegistrations.map((registration) => (
                  <tr key={registration.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div
                        onClick={() => handleCheckboxChange(registration.id)}
                        className="flex items-center cursor-pointer"
                      >
                        <SafeIcon
                          icon={selectedRegistrations.includes(registration.id) ? FiCheckSquare : FiSquare}
                          className={selectedRegistrations.includes(registration.id) ? "text-primary-500" : "text-gray-400 hover:text-gray-600"}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {registration.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {registration.email || registration.phone || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {registration.day || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {registration.menu_name || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {registration.menu_date ? formatMenuDate(registration.menu_date) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SafeIcon icon={FiUsers} className="mr-2 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {registration.persons || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SafeIcon icon={FiClock} className="mr-2 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {registration.time || '-'} Uhr
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-primary-600">
                        {registration.total_price ? `CHF ${registration.total_price.toFixed(2)}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SafeIcon icon={FiCalendar} className="mr-2 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {formatDate(registration.registration_date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-block px-2 py-1 rounded-full text-xs ${
                        registration.is_next_week 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {registration.is_next_week ? 'Nächste Woche' : 'Aktuelle Woche'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(registration)}
                          className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                          title="Anmeldung bearbeiten"
                        >
                          <SafeIcon icon={FiEdit} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteClick(registration)}
                          className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                          title="Anmeldung löschen"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <div className="flex flex-col items-center justify-center">
            <p className="text-gray-500 mb-2">Keine Anmeldungen gefunden</p>
            <p className="text-gray-400 text-sm">Versuchen Sie andere Suchkriterien</p>
          </div>
        </div>
      )}

      {/* Bearbeitungs-Modal */}
      {editingRegistration && (
        <EditRegistrationModal
          registration={editingRegistration}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}

      {/* Einzelnes Lösch-Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full"
          >
            {deleteSuccess ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Erfolgreich gelöscht</h3>
                <p className="text-sm text-gray-500">Die Anmeldung wurde erfolgreich gelöscht.</p>
              </div>
            ) : (
              <>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <SafeIcon icon={FiAlertCircle} className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Anmeldung löschen</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Sind Sie sicher, dass Sie diese Anmeldung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                      </p>
                      {registrationToDelete && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium">{registrationToDelete.name}</p>
                          <p className="text-xs text-gray-500">{registrationToDelete.day} - {registrationToDelete.menu_name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {registrationToDelete.persons} {registrationToDelete.persons === 1 ? 'Person' : 'Personen'} | {' '}
                            {formatDate(registrationToDelete.registration_date)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    disabled={deleteLoading}
                    className={`w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                      deleteLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    onClick={handleDelete}
                  >
                    {deleteLoading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                        Löschen...
                      </>
                    ) : (
                      'Löschen'
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    disabled={deleteLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => setIsDeleteModalOpen(false)}
                  >
                    <SafeIcon icon={FiX} className="mr-2" />
                    Abbrechen
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Mehrfach-Lösch-Modal */}
      {isMultiDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full"
          >
            {multiDeleteSuccess ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Erfolgreich gelöscht</h3>
                <p className="text-sm text-gray-500">
                  {selectedRegistrations.length} {selectedRegistrations.length === 1 ? 'Anmeldung wurde' : 'Anmeldungen wurden'} erfolgreich gelöscht.
                </p>
              </div>
            ) : (
              <>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <SafeIcon icon={FiAlertCircle} className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Mehrere Anmeldungen löschen</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Sind Sie sicher, dass Sie {selectedRegistrations.length} {selectedRegistrations.length === 1 ? 'Anmeldung' : 'Anmeldungen'} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                      </p>
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium">Ausgewählte Anmeldungen:</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedRegistrations.length} von {filteredRegistrations.length} {filteredRegistrations.length === 1 ? 'Anmeldung' : 'Anmeldungen'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    disabled={multiDeleteLoading}
                    className={`w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                      multiDeleteLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    onClick={handleMultiDelete}
                  >
                    {multiDeleteLoading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                        Löschen...
                      </>
                    ) : (
                      'Alle löschen'
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    disabled={multiDeleteLoading}
                    className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={() => setIsMultiDeleteModalOpen(false)}
                  >
                    <SafeIcon icon={FiX} className="mr-2" />
                    Abbrechen
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Registrations;